"use client";
import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { motion, useReducedMotion, useSpring } from "framer-motion";
import {
  TrendingUp,
  FileText,
  ArrowRight,
  CheckCircle2,
  Users,
  Wallet,
  Briefcase,
  RotateCcw,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import RichText from "./RichText";

/* ── Assumption constants (kept together so the maths stays auditable) ───────── */
const FEE_RECOVERY_RATE = 0.045; // share of annual fee volume recovered via automated reminders
const HOURS_PER_STUDENT = 7.5; // attendance, report cards, fee follow-ups
const HOURS_PER_STAFF = 95; // admin paperwork, registers, circulars
const PAPER_COST_PER_STUDENT = 240; // printed circulars, marksheets, receipts

/* `labelKey` rather than a label: the numbers are the maths and must not move
   with the language, so only the caption is looked up. */
const SLIDERS = [
  { key: "students", labelKey: "roi.sliderStudents", icon: Users, min: 100, max: 3500, step: 25 },
  { key: "monthlyFee", labelKey: "roi.sliderMonthlyFee", icon: Wallet, min: 800, max: 12000, step: 200 },
  { key: "staff", labelKey: "roi.sliderStaff", icon: Briefcase, min: 10, max: 180, step: 5 },
] as const;

/* `id` is the stable identity used for the active-preset check and the React
   key — the visible label is translated. */
const PRESETS = [
  { id: "small", labelKey: "roi.presetSmall", students: 300, monthlyFee: 1800, staff: 18 },
  { id: "growing", labelKey: "roi.presetGrowing", students: 650, monthlyFee: 3500, staff: 35 },
  { id: "large", labelKey: "roi.presetLarge", students: 1400, monthlyFee: 5500, staff: 80 },
  { id: "group", labelKey: "roi.presetGroup", students: 2800, monthlyFee: 8000, staff: 150 },
];

const DEFAULTS = PRESETS[1]; // "Growing" — the landing state

/* ── Formatters ──────────────────────────────────────────────────────── */
/* Digits stay Western and the grouping stays Indian (lakh/crore) in both
   languages — that is how rupee figures are written on an Indian fee receipt
   whichever language surrounds them. Only the scale WORD is translated, which
   is why it's a parameter rather than a literal. */
const formatINR = (val: number, units: { crore: string; lakh: string }) => {
  const v = Math.round(val);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} ${units.crore}`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)} ${units.lakh}`;
  return `₹${v.toLocaleString("en-IN")}`;
};
const formatCount = (val: number) => Math.round(val).toLocaleString("en-IN");

/** Number that eases to its new value instead of snapping — makes the panel feel live. */
function AnimatedValue({ value, format }: { value: number; format: (n: number) => string }) {
  const reduceMotion = useReducedMotion();
  const spring = useSpring(value, { stiffness: 110, damping: 22, mass: 0.6 });
  const [display, setDisplay] = useState(() => format(value));

  useEffect(() => {
    // Counting digits is pure motion — jump straight to the value when it's unwelcome.
    if (reduceMotion) spring.jump(value);
    else spring.set(value);
  }, [value, spring, reduceMotion]);

  useEffect(() => {
    // Re-run the CURRENT value through the formatter before subscribing. The
    // spring only emits on change, so swapping `format` alone — which is what
    // a language switch does, while every number stays put — would otherwise
    // leave the previous language's label frozen on screen ("₹13.8 Lakhs"
    // sitting in the middle of the Hindi panel).
    setDisplay(format(spring.get()));
    return spring.on("change", (v) => setDisplay(format(v)));
  }, [spring, format]);

  return <span className="tabular-nums">{display}</span>;
}

export default function ROICalculator() {
  const { t, tf } = useLanguage();
  const [students, setStudents] = useState<number>(650);
  const [monthlyFee, setMonthlyFee] = useState<number>(3500);
  const [staff, setStaff] = useState<number>(35);

  const values = { students, monthlyFee, staff };
  const setters = { students: setStudents, monthlyFee: setMonthlyFee, staff: setStaff };

  /* AnimatedValue subscribes to its `format` in an effect, so these have to
     keep their identity between renders or every keystroke on a slider would
     tear down and rebuild the spring subscription. They only change when the
     language does. */
  const formatMoney = useCallback(
    (v: number) => formatINR(v, { crore: t("roi.crore"), lakh: t("roi.lakh") }),
    [t]
  );
  const formatHours = useCallback(
    (v: number) => `${formatCount(v)} ${t("roi.hoursSuffix")}`,
    [t]
  );

  const applyPreset = useCallback((p: (typeof PRESETS)[number]) => {
    setStudents(p.students);
    setMonthlyFee(p.monthlyFee);
    setStaff(p.staff);
  }, []);

  const activePreset = PRESETS.find(
    (p) => p.students === students && p.monthlyFee === monthlyFee && p.staff === staff
  )?.id;

  const isDirty =
    students !== DEFAULTS.students ||
    monthlyFee !== DEFAULTS.monthlyFee ||
    staff !== DEFAULTS.staff;

  // 1. Uncollected fees recovered via built-in automated fee tracking & online payment links
  const annualFeeVolume = students * monthlyFee * 12;
  const estimatedFeeRecovered = Math.round((annualFeeVolume * FEE_RECOVERY_RATE) / 1000) * 1000;

  // 2. Teacher & admin hours returned to teaching — scales with both roll size and headcount
  const hoursSavedPerYear = Math.round(students * HOURS_PER_STUDENT + staff * HOURS_PER_STAFF);
  const hoursPerStaff = Math.round(hoursSavedPerYear / staff);

  // 3. Paper, print & courier costs avoided
  const paperSavingsPerYear = Math.round(students * PAPER_COST_PER_STUDENT);

  const totalValue = estimatedFeeRecovered + paperSavingsPerYear;
  const perStudentValue = Math.round(totalValue / students);

  // Only the two rupee figures belong here — saved staff hours are a separate
  // kind of gain and adding them to a money subtotal would be nonsense. Keeping
  // this list to the money means the breakdown visibly sums to the headline.
  const moneyRows = [
    {
      key: "fees",
      icon: TrendingUp,
      label: t("roi.rowFeesLabel"),
      value: estimatedFeeRecovered,
      note: t("roi.rowFeesNote"),
    },
    {
      key: "paper",
      icon: FileText,
      label: t("roi.rowPaperLabel"),
      value: paperSavingsPerYear,
      note: t("roi.rowPaperNote"),
    },
  ];

  return (
    <section
      id="roi-calculator"
      className="w-full scroll-mt-24 py-6 sm:py-16 lg:py-10 xl:py-12 bg-[#FAFCFF] overflow-hidden relative lg:min-h-screen lg:flex lg:items-center"
    >
      {/* Background accents */}
      <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />
      <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-[#2A48C9]/[0.06] rounded-full blur-[140px] pointer-events-none -translate-y-1/2 -translate-x-1/3" />
      <div className="absolute bottom-0 right-0 w-[450px] h-[450px] bg-[#687FE7]/[0.05] rounded-full blur-[120px] pointer-events-none translate-x-1/4" />

      <div className="site-container relative z-10 px-3 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-4 sm:mb-10 lg:mb-7 xl:mb-9"
        >
          <h2 className="font-heading text-xl sm:text-4xl lg:text-3xl font-extrabold text-[#0d1117] mb-1.5 sm:mb-3 tracking-tight">
            <RichText text={t("roi.heading")} />
          </h2>

          <p className="text-xs sm:text-lg lg:text-[15px] xl:text-base text-slate-600 font-medium leading-normal sm:leading-relaxed max-w-2xl mx-auto">
            {t("roi.body")}
          </p>
        </motion.div>

        {/* Capped well short of the 1720px site container — past ~1100px the two
            panels stop reading as a compact widget and just stretch. */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5 sm:gap-6 lg:gap-6 xl:gap-7 items-stretch max-w-[1120px] mx-auto">
          {/* ── Left: inputs ────────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-6 bg-white p-3.5 sm:p-7 lg:p-6 rounded-[16px] sm:rounded-[26px] border border-slate-100 shadow-[0_15px_45px_rgba(0,0,0,0.04)] flex flex-col"
          >
            <div className="flex items-baseline justify-between gap-3 mb-2.5 sm:mb-5 lg:mb-4">
              <h3 className="font-sora font-bold text-[15px] sm:text-xl text-[#0d1117]">
                {t("roi.panelTitle")}
              </h3>
              {isDirty && (
                <button
                  type="button"
                  onClick={() => applyPreset(DEFAULTS)}
                  className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-semibold text-slate-400 hover:text-[#3D5EE1] transition-colors whitespace-nowrap rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2A48C9]/40 focus-visible:ring-offset-2"
                >
                  <RotateCcw size={11} aria-hidden />
                  {t("roi.reset")}
                </button>
              )}
            </div>

            {/* Presets — a faster way in than dragging three sliders */}
            <div className="mb-3 sm:mb-6 lg:mb-5">
              <span className="block text-[9px] sm:text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-400 mb-1.5">
                {t("roi.closestMatch")}
              </span>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {PRESETS.map((p) => {
                  const active = activePreset === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => applyPreset(p)}
                      aria-pressed={active}
                      className={`px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold border transition-all active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2A48C9]/40 focus-visible:ring-offset-2 ${
                        active
                          ? "bg-[#3D5EE1] border-[#3D5EE1] text-white shadow-[0_4px_12px_-4px_rgba(61,94,225,0.6)]"
                          : "bg-white border-slate-200 text-slate-500 hover:border-[#2A48C9]/40 hover:text-[#3D5EE1]"
                      }`}
                    >
                      {t(p.labelKey)}
                    </button>
                  );
                })}
                {!activePreset && (
                  <span className="px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold border border-dashed border-[#2A48C9]/40 text-[#3D5EE1] bg-[#2A48C9]/[0.06]">
                    {t("roi.custom")}
                  </span>
                )}
              </div>
            </div>

            {/* justify-between spreads the three sliders into whatever height the
                taller results panel forces, instead of leaving a dead gap above
                the disclaimer. */}
            <div className="flex-1 flex flex-col justify-between gap-3 sm:gap-6 lg:gap-5">
              {SLIDERS.map(({ key, labelKey, icon: Icon, min, max, step }) => {
                const value = values[key];
                const fill = ((value - min) / (max - min)) * 100;
                const display =
                  key === "monthlyFee"
                    ? `₹${value.toLocaleString("en-IN")}`
                    : value.toLocaleString("en-IN");
                /* The suffix under each end of the track. `monthlyFee` shows a
                   rate, the other two show a count of things. */
                const unit =
                  key === "students"
                    ? ` ${t("roi.unitStudents")}`
                    : key === "staff"
                      ? ` ${t("roi.unitStaff")}`
                      : t("roi.unitPerMonth");

                return (
                  <div key={key}>
                    <div className="flex justify-between items-center gap-2 mb-1.5 sm:mb-3">
                      <label
                        htmlFor={`roi-${key}`}
                        className="flex items-center gap-1.5 text-[11px] sm:text-sm font-semibold text-slate-600"
                      >
                        <Icon size={13} className="text-[#3D5EE1] shrink-0" />
                        {t(labelKey)}
                      </label>
                      <span className="font-sora font-bold text-[14px] sm:text-xl text-[#1E3BB3] tabular-nums leading-none">
                        {display}
                        {key === "monthlyFee" && (
                          <span className="text-[9px] sm:text-xs font-semibold text-slate-400 ml-0.5">
                            {t("roi.unitPerMonth")}
                          </span>
                        )}
                      </span>
                    </div>

                    <input
                      id={`roi-${key}`}
                      type="range"
                      min={min}
                      max={max}
                      step={step}
                      value={value}
                      aria-valuetext={
                        key === "monthlyFee"
                          ? tf("roi.ariaPerMonth", { value: `₹${value.toLocaleString("en-IN")}` })
                          // `unit` already carries its leading space.
                          : `${value.toLocaleString("en-IN")}${unit}`
                      }
                      onChange={(e) => setters[key](Number(e.target.value))}
                      style={{ "--fill": `${fill}%` } as React.CSSProperties}
                      className="roi-slider"
                    />

                    <div className="flex justify-between text-[9px] sm:text-xs font-medium text-slate-400 mt-0.5">
                      <span>
                        {key === "monthlyFee" ? `₹${min.toLocaleString("en-IN")}` : min}
                        {unit}
                      </span>
                      <span>
                        {key === "monthlyFee"
                          ? `₹${max.toLocaleString("en-IN")}`
                          : `${max.toLocaleString("en-IN")}+`}
                        {unit}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 pt-2.5 sm:mt-6 sm:pt-4 border-t border-slate-100 flex items-start gap-1.5 text-[9.5px] sm:text-xs lg:text-[11px] font-medium text-slate-500">
              <CheckCircle2 size={13} className="text-[#3D5EE1] shrink-0 mt-[1px]" />
              <span className="leading-snug">
                {t("roi.disclaimer")}
              </span>
            </div>
          </motion.div>

          {/* ── Right: results ───────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-6 bg-gradient-to-br from-[#0F1E61] via-[#1E3BB3] to-[#2647DD] p-3.5 sm:p-7 lg:p-6 rounded-[16px] sm:rounded-[26px] text-white shadow-[0_25px_60px_-15px_rgba(61,94,225,0.4)] flex flex-col relative overflow-hidden"
          >
            <div className="absolute inset-0 roi-dot-grid opacity-[0.55] pointer-events-none [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]" />
            <div className="absolute -top-24 -right-16 w-72 h-72 bg-[#4DD0C4]/25 rounded-full blur-[90px] pointer-events-none" />

            <div className="relative z-10 flex-1 flex flex-col">
              <span className="block text-[9px] sm:text-[11px] uppercase tracking-[0.16em] font-semibold text-brand-200/90 mb-2.5 sm:mb-5 lg:mb-4">
                {t("roi.gainsLabel")}
              </span>

              {/* The two gains, side by side. They used to be buried in one
                  sentence together with the per-student figure; money and time
                  are different things, so they get one column each. */}
              <div className="grid grid-cols-2 gap-2 sm:gap-5 mb-3 sm:mb-6 lg:mb-5">
                <div>
                  {/* Sized to keep the longest value ("₹13.8 Lakhs") on one
                      line in a half-width column — a wrap here knocks the two
                      captions out of alignment. */}
                  <div className="font-sora font-black text-[18px] min-[380px]:text-[20px] min-[440px]:text-[24px] sm:text-[40px] lg:text-[33px] xl:text-[40px] leading-[1.05] tracking-tight whitespace-nowrap">
                    <AnimatedValue value={totalValue} format={formatMoney} />
                  </div>
                  <p className="text-[11px] sm:text-sm lg:text-[12.5px] xl:text-sm font-semibold text-white/90 mt-1">
                    {t("roi.moneyBack")}
                  </p>
                  <p className="text-[9.5px] sm:text-xs lg:text-[11px] text-brand-100/65 mt-0.5">
                    {tf("roi.moneyBackNote", { value: formatMoney(totalValue / 12) })}
                  </p>
                </div>

                <div className="pl-2.5 sm:pl-5 border-l border-white/15">
                  {/* Sized to keep the longest value ("₹13.8 Lakhs") on one
                      line in a half-width column — a wrap here knocks the two
                      captions out of alignment. */}
                  <div className="font-sora font-black text-[18px] min-[380px]:text-[20px] min-[440px]:text-[24px] sm:text-[40px] lg:text-[33px] xl:text-[40px] leading-[1.05] tracking-tight whitespace-nowrap">
                    <AnimatedValue
                      value={hoursSavedPerYear}
                      format={formatHours}
                    />
                  </div>
                  <p className="text-[11px] sm:text-sm lg:text-[12.5px] xl:text-sm font-semibold text-white/90 mt-1">
                    {t("roi.staffTime")}
                  </p>
                  <p className="text-[9.5px] sm:text-xs lg:text-[11px] text-brand-100/65 mt-0.5">
                    {tf("roi.staffTimeNote", { value: hoursPerStaff.toLocaleString("en-IN") })}
                  </p>
                </div>
              </div>

              {/* Where the money comes from — two rows that add up to the
                  headline figure, which is what the old percentage bar was
                  trying to say with a chart. */}
              <div className="border-t border-white/10 pt-2.5 sm:pt-5 lg:pt-4">
                <span className="block text-[9px] sm:text-[11px] uppercase tracking-[0.16em] font-semibold text-brand-200/90 mb-0.5">
                  {t("roi.whereFrom")}
                </span>

                <div className="divide-y divide-white/10">
                  {moneyRows.map(({ key, icon: Icon, label, value, note }) => (
                    <div key={key} className="py-2 sm:py-3.5 lg:py-2.5 xl:py-3.5 group">
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 text-[11px] sm:text-sm font-semibold text-brand-50/90">
                          <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center shrink-0 group-hover:bg-white/20 transition-colors">
                            <Icon size={12} className="text-brand-100" />
                          </span>
                          {label}
                        </span>
                        <span className="font-sora font-bold text-[14px] sm:text-[21px] lg:text-[20px] xl:text-[21px] text-white leading-none whitespace-nowrap">
                          <AnimatedValue value={value} format={formatMoney} />
                        </span>
                      </div>
                      <p className="text-[9.5px] sm:text-xs lg:text-[11px] text-brand-100/60 leading-snug mt-1 sm:mt-1.5 pl-[32px] sm:pl-[42px]">
                        {note}
                      </p>
                    </div>
                  ))}
                </div>

                <p className="text-[10px] sm:text-xs lg:text-[11.5px] text-brand-100/80 font-medium border-t border-white/10 pt-2 sm:pt-3 mt-1">
                  {t("roi.perStudentBefore")}
                  <span className="text-white font-bold">
                    <AnimatedValue value={perStudentValue} format={formatMoney} />
                  </span>
                  {t("roi.perStudentAfter")}
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="relative z-10 pt-3 sm:pt-6 lg:pt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
              <span className="text-[10px] sm:text-xs text-brand-100/80 font-medium max-w-[230px] leading-snug">
                {t("roi.ctaNote")}
              </span>
              {/* /contact, not `#contact` — the `id="contact"` section is in
                  DemoSection, which this page never renders. */}
              <Link
                href="/contact"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 sm:px-5 sm:py-3 rounded-xl bg-white text-[#00483C] font-sora font-bold text-[11.5px] sm:text-[13px] shadow-[0_12px_30px_-10px_rgba(0,0,0,0.5)] hover:bg-brand-50 transition-all active:scale-95 group"
              >
                <span>{t("roi.ctaButton")}</span>
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

